import { useMemo, type FC } from 'react';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

import {
  getApiAdminrewardsGetCollectionQueryKey,
  useApiAdminrewardsIdPatch
} from '@r/api/generated/client.ts';
import { apiAdminrewardsIdPatchBody } from '@r/api/generated-zod/index.ts';
import type {
  LogicTypeStepWorkspaceReadModel,
  RewardGroupedRewardReadModel,
  RewardRewardPatchJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';
import { getRewardProductIri, useRewardShopProducts } from './reward-product-utils.ts';

/** Validation : un produit boutique reward doit être sélectionné. */
function buildRewardProductFormSchema(t: TFunction) {
  return z.object({
    productId: z.string().min(1, t('admin.settings.steps.rewards.update.form.product.required'))
  });
}

type RewardProductFormValues = z.infer<ReturnType<typeof buildRewardProductFormSchema>>;

interface UpdateStepRewardFormProps {
  reward: RewardGroupedRewardReadModel;
  step: LogicTypeStepWorkspaceReadModel;
  rewardShopId: number | null;
  rewardShopLoading: boolean;
  onSuccess: () => void;
}

/** Associe un produit de la boutique reward à une récompense (IRI shop-scoped). */
export const UpdateStepRewardForm: FC<UpdateStepRewardFormProps> = ({
  reward,
  step,
  rewardShopId,
  rewardShopLoading,
  onSuccess
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const patchRewardMutation = useApiAdminrewardsIdPatch();
  const formSchema = useMemo(() => buildRewardProductFormSchema(t), [t]);

  const productsQuery = useRewardShopProducts(rewardShopId, rewardShopId != null);
  const products = useMemo(
    () =>
      [...(productsQuery.data ?? [])].sort((left, right) =>
        left.name.localeCompare(right.name, 'fr')
      ),
    [productsQuery.data]
  );

  const defaultProductId = reward.product?.id != null ? String(reward.product.id) : '';

  const form = useForm<RewardProductFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { productId: defaultProductId }
  });

  const isLoadingProducts = rewardShopLoading || productsQuery.isLoading;
  const isShopMissing = !rewardShopLoading && rewardShopId == null;

  function onSubmit(values: RewardProductFormValues) {
    if (reward.id == null || rewardShopId == null) {
      toast.error(t('admin.settings.steps.rewards.update.error'));
      return;
    }

    const patchData: RewardRewardPatchJsonMergePatchModel = {
      product: getRewardProductIri(Number(values.productId), rewardShopId)
    };

    const parsed = apiAdminrewardsIdPatchBody.safeParse(patchData);
    if (!parsed.success) {
      toast.error(t('admin.settings.steps.rewards.update.error'));
      return;
    }

    patchRewardMutation.mutate(
      { id: String(reward.id), data: patchData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getApiAdminrewardsGetCollectionQueryKey() });
          toast.success(t('admin.settings.steps.rewards.update.success'));
          onSuccess();
        },
        onError: (error) => {
          if (isOrvalErrorFromPath(error, 'patch', '/admin/rewards')) {
            showOrvalErrorToast(error, t('admin.settings.steps.rewards.update.error'));
          } else {
            toast.error(t('admin.settings.steps.rewards.update.error'));
          }
        }
      }
    );
  }

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormItem>
          <FormLabel>{t('admin.settings.steps.rewards.column.typology')}</FormLabel>
          <FormControl>
            <Input readOnly disabled value={reward.typology?.name ?? ''} />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>{t('form.step.name.label')}</FormLabel>
          <FormControl>
            <Input readOnly disabled value={step.name ?? ''} />
          </FormControl>
        </FormItem>

        <FormField
          control={form.control}
          name="productId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.settings.steps.rewards.update.form.product.label')}</FormLabel>
              <FormControl>
                <Select
                  disabled={isLoadingProducts || isShopMissing || patchRewardMutation.isPending}
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        'admin.settings.steps.rewards.update.form.product.placeholder'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={String(product.id)}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isShopMissing ? (
          <p className="text-sm text-destructive">
            {t('admin.settings.steps.rewards.update.no.shop')}
          </p>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          loading={patchRewardMutation.isPending}
          disabled={isLoadingProducts || isShopMissing || products.length === 0}
        >
          <Save className="mr-2 size-4" />
          {t('admin.settings.steps.rewards.update.form.submit')}
        </Button>
      </form>
    </Form>
  );
};
